import React, { useMemo, useRef } from 'react'
import {
  useSequencerStore,
  selectCurrentProject,
  isRichStep,
  PADS_PER_PROJECT,
  STEP_COUNTS,
  type StepCell,
  type StepCount,
  type LayoutMode,
  type TrackerRow,
} from '@/stores/sequencerStore'
import { KITS, type KitVoice } from '@/hooks/useAudioEngine'
import { resolvePad, type ResolvedPad } from '@/lib/resolvePad'
import { TrackerLayout } from './tracker/TrackerLayout'

/**
 * How long a pointer must stay down on a step cell before we interpret
 * it as "open the per-step inspector" rather than "toggle on/off".
 * 320ms matches iOS long-press conventions (UIGestureRecognizer default
 * 0.5s feels sluggish here since users are already tapping quickly
 * between cells). Right-click / ctrl+click fires the same handler
 * immediately as a desktop affordance.
 */
const LONG_PRESS_MS = 320

/**
 * Step editor. Five layout modes, all reading from the same project
 * data. Each sub-layout component owns its own presentation; the
 * dispatcher here picks based on the store's `layoutMode`.
 *
 *  - Bars    — 4 rows of 16 steps, focused pad × full stepCount
 *  - Tracks  — 4 rows, pads 0..3 × 16 steps each (parallel)
 *  - Grid    — 8×8, focused pad × 64 steps
 *  - Quads   — 8×8 split into four 4×4 quadrants, pads 0..3
 *  - Tracker — vertical grid + inspector; per-step rich editing
 */
interface StepGridProps {
  /**
   * Called when the tracker layout's "+ Add channel" action is
   * invoked. The payload is the first empty pad index (or null
   * when the grid is full). Other layouts ignore this prop.
   */
  onEditPad?: (padIndex: number | null) => void
}

export const StepGrid: React.FC<StepGridProps> = ({ onEditPad }) => {
  const layoutMode = useSequencerStore(s => s.layoutMode)
  const project = useSequencerStore(selectCurrentProject)
  const kit = KITS[project.kitId]

  if (layoutMode === 'tracker') {
    return <TrackerLayout onEditPad={onEditPad ?? (() => {})} />
  }

  const common = {
    project,
    kit,
  }

  switch (layoutMode) {
    case 'tracks':
      return <TracksLayout {...common} />
    case 'grid':
      return <GridLayout {...common} />
    case 'quads':
      return <QuadsLayout {...common} />
    default:
      return <GridLayout {...common} />
  }
}

interface LayoutProps {
  project: ReturnType<typeof selectCurrentProject>
  kit: { label: string; voices: KitVoice[] }
}

// ── Tracks (all occupied pads × 16 steps, parallel, scroll-snap) ──
//
// Phase 6 of the sequencer overhaul. Previously capped at 4 kit
// pads; now shows every occupied slot (up to `PADS_PER_PROJECT`) +
// every custom MIDI pad, vertically scrollable with CSS
// scroll-snap so each track lands on a snap point. Users can flick
// through all 16+ tracks without leaving Tracks mode.
//
// Row height is fixed so snap points line up evenly. The scroll
// container caps at a viewport-proportional max-height so it
// doesn't push the FX rail off-screen when many tracks are active.
//
// Each row holds 2 sub-rows of 8 step cells (the "2×8" in the layout
// name) — the row needs enough vertical room for both sub-rows to
// render as square-ish cells instead of getting squashed into thin
// rectangles. `h-28` (112px) leaves ~50px per sub-row after gaps,
// which matches Koala-style pad density and gives comfortable iPad
// touch targets while still letting ~8 tracks fit in a native window
// before the scroll-snap picks up.

const TRACK_ROW_CLASSES = 'flex items-center gap-3 snap-start h-28 shrink-0'

const TracksLayout: React.FC<LayoutProps> = ({ project, kit }) => {
  const isPlaying = useSequencerStore(s => s.isPlaying)
  const currentStep = useSequencerStore(s => s.currentStep)
  const toggleStep = useSequencerStore(s => s.toggleStep)

  // Only ever show steps 0..15 in Tracks mode (one bar per pad). The
  // project may still have 32/64 steps — those are reachable in
  // Grid mode; Tracks trades depth for parallel-pad visibility.
  const visibleSteps = Math.min(project.stepCount, 16)

  // Resolve every pad with the same padDefs → kit-voice → null
  // precedence PadGrid and TrackerLayout use, so a sample/synth
  // override on pad 9 shows up in Tracks mode too. Occupied slots
  // become rows; empty slots are dropped.
  const kitRows = useMemo(() => {
    const kitSlots: Array<(typeof kit.voices)[number] | undefined> = new Array(
      PADS_PER_PROJECT,
    ).fill(undefined)
    kit.voices.forEach((v, i) => {
      kitSlots[i] = v
    })
    const resolved = Array.from({ length: PADS_PER_PROJECT }, (_, i) =>
      resolvePad(project, kitSlots, i),
    )
    return resolved
      .map((pad, padIndex) => ({ pad, padIndex }))
      .filter(
        (r): r is { pad: ResolvedPad; padIndex: number } => r.pad !== null,
      )
  }, [kit, project])

  const totalRows = kitRows.length + project.midiPads.length

  return (
    <StepGridShell
      title={`Step grid · ${totalRows} track${totalRows === 1 ? '' : 's'}`}
      note="2×8 layout — scroll to reveal more tracks"
    >
      <div
        className="flex flex-col gap-2 overflow-y-auto snap-y snap-mandatory overscroll-contain max-h-[calc(100dvh-22rem)] min-h-[14rem] pr-1"
        role="group"
        aria-label="Tracks — scroll vertically"
      >
        {kitRows.map(({ pad, padIndex }) => {
          const row = project.steps[padIndex] ?? []
          return (
            <div key={padIndex} className={TRACK_ROW_CLASSES}>
              <span className="w-16 shrink-0 text-[10px] font-mono uppercase tracking-wider text-content-tertiary truncate">
                {pad.label}
              </span>
              <div className="flex-1 h-full min-w-0 grid grid-cols-8 sm:grid-cols-16 grid-rows-2 sm:grid-rows-1 auto-rows-fr gap-1.5 overflow-hidden">
                {Array.from({ length: visibleSteps }).map((_, stepIdx) => (
                  <StepCellButton
                    key={stepIdx}
                    cell={row[stepIdx]}
                    stepIndex={stepIdx}
                    isDownbeat={stepIdx % 4 === 0}
                    isPlayhead={isPlaying && currentStep === stepIdx}
                    size="fill"
                    trackerRow={{ kind: 'kit', padIndex }}
                    onToggle={() =>
                      toggleStep({ kind: 'kit', padIndex }, stepIdx)
                    }
                  />
                ))}
              </div>
            </div>
          )
        })}
        {project.midiPads.map(mp => {
          const row = project.midiPadSteps[mp.id] ?? []
          return (
            <div key={mp.id} className={TRACK_ROW_CLASSES}>
              <span
                className="w-16 shrink-0 text-[10px] font-mono uppercase tracking-wider text-second truncate"
                title={`${mp.label} · note ${mp.note} · ch ${mp.channel}`}
              >
                {mp.label}
              </span>
              <div className="flex-1 h-full min-w-0 grid grid-cols-8 sm:grid-cols-16 grid-rows-2 sm:grid-rows-1 auto-rows-fr gap-1.5 overflow-hidden">
                {Array.from({ length: visibleSteps }).map((_, stepIdx) => (
                  <StepCellButton
                    key={stepIdx}
                    cell={row[stepIdx]}
                    stepIndex={stepIdx}
                    isDownbeat={stepIdx % 4 === 0}
                    isPlayhead={isPlaying && currentStep === stepIdx}
                    accent="second"
                    size="fill"
                    trackerRow={{ kind: 'midi', padId: mp.id }}
                    onToggle={() =>
                      toggleStep({ kind: 'midi', padId: mp.id }, stepIdx)
                    }
                  />
                ))}
              </div>
            </div>
          )
        })}
        {totalRows === 0 && (
          <p className="text-[11px] font-mono text-content-tertiary text-center py-6">
            No tracks yet — tap a pad in the grid (or add a custom MIDI pad) to
            create one.
          </p>
        )}
      </div>
    </StepGridShell>
  )
}

// ── Grid (8×8, one pad × 64 steps) ──────────────────────────

const GridLayout: React.FC<LayoutProps> = ({ project, kit }) => {
  const focusedPad = useSequencerStore(s => s.focusedPad)
  const isPlaying = useSequencerStore(s => s.isPlaying)
  const currentStep = useSequencerStore(s => s.currentStep)
  const toggleStep = useSequencerStore(s => s.toggleStep)
  const setStepCount = useSequencerStore(s => s.setStepCount)

  const row = project.steps[focusedPad] ?? []
  const voice = kit.voices[focusedPad]
  const label = voice?.label ?? `Pad ${focusedPad + 1}`
  const totalSteps = project.stepCount

  // Grid renders 64 slots — smaller projects show the out-of-range
  // slots as faint click-to-extend buttons rather than inert cells.
  return (
    <StepGridShell title={`Step grid · ${label}`}>
      <div className="grid grid-cols-8 gap-1.5">
        {Array.from({ length: 64 }).map((_, stepIdx) => {
          if (stepIdx >= totalSteps) {
            const next = nextStepCountFor(stepIdx)
            return (
              <button
                key={stepIdx}
                type="button"
                onClick={() => setStepCount(next)}
                aria-label={`Extend to ${next} steps`}
                title={`Extend to ${next} steps`}
                className="aspect-square rounded-md border border-dashed border-brd-subtle/60 bg-surface-raised/20 opacity-50 hover:opacity-100 hover:border-[color:var(--accent-halo)] hover:bg-surface-raised/40 hover:text-accent transition-colors text-content-tertiary font-mono text-[10px] flex items-center justify-center"
              >
                {stepIdx + 1}
              </button>
            )
          }
          return (
            <StepCellButton
              key={stepIdx}
              cell={row[stepIdx]}
              stepIndex={stepIdx}
              isDownbeat={stepIdx % 4 === 0}
              isPlayhead={isPlaying && currentStep === stepIdx}
              trackerRow={{ kind: 'kit', padIndex: focusedPad }}
              onToggle={() =>
                toggleStep({ kind: 'kit', padIndex: focusedPad }, stepIdx)
              }
            />
          )
        })}
      </div>
    </StepGridShell>
  )
}

// ── Quads (4×4 layout — all occupied pads in 4-at-a-time groups) ──
//
// Previously hardcoded to pads 0–3; pads 4+ were unreachable in this
// mode, which is wrong when the kit or custom MIDI pads extend past
// the first four. Now we render every occupied pad + every custom
// MIDI pad into vertical-scrolling groups of 4, with scroll-snap so
// each group lands on a snap point. Matches the Tracks layout's
// "show all occupied rows" convention.

const QuadsLayout: React.FC<LayoutProps> = ({ project, kit }) => {
  const isPlaying = useSequencerStore(s => s.isPlaying)
  const currentStep = useSequencerStore(s => s.currentStep)
  const toggleStep = useSequencerStore(s => s.toggleStep)
  const setStepCount = useSequencerStore(s => s.setStepCount)

  const visibleSteps = Math.min(project.stepCount, 16)

  // Build a flat list of { label, onToggle, row, trackerRow } entries
  // for every occupied kit pad, then append every custom MIDI pad. We
  // chunk into groups of 4 below for the 2×2 quadrant visual.
  type QuadPad = {
    key: string
    label: string
    accent?: 'accent' | 'second'
    row: StepCell[]
    trackerRow: TrackerRow
    onToggle: (stepIdx: number) => void
  }
  const pads = useMemo<QuadPad[]>(() => {
    const kitSlots: Array<(typeof kit.voices)[number] | undefined> = new Array(
      PADS_PER_PROJECT,
    ).fill(undefined)
    kit.voices.forEach((v, i) => {
      kitSlots[i] = v
    })
    const kitResolved = Array.from({ length: PADS_PER_PROJECT }, (_, i) =>
      resolvePad(project, kitSlots, i),
    )
    const list: QuadPad[] = []
    kitResolved.forEach((pad, padIndex) => {
      if (!pad) return
      list.push({
        key: `kit-${padIndex}`,
        label: pad.label,
        row: project.steps[padIndex] ?? [],
        trackerRow: { kind: 'kit', padIndex },
        onToggle: (stepIdx: number) =>
          toggleStep({ kind: 'kit', padIndex }, stepIdx),
      })
    })
    for (const mp of project.midiPads) {
      list.push({
        key: `midi-${mp.id}`,
        label: mp.label,
        accent: 'second',
        row: project.midiPadSteps[mp.id] ?? [],
        trackerRow: { kind: 'midi', padId: mp.id },
        onToggle: (stepIdx: number) =>
          toggleStep({ kind: 'midi', padId: mp.id }, stepIdx),
      })
    }
    return list
  }, [kit, project, toggleStep])

  const groups: QuadPad[][] = []
  for (let i = 0; i < pads.length; i += 4) {
    groups.push(pads.slice(i, i + 4))
  }

  return (
    <StepGridShell
      title={`Step grid · ${pads.length} pad${pads.length === 1 ? '' : 's'} in quads`}
      note={groups.length > 1 ? '4×4 · scroll for more pads' : '4×4 layout'}
    >
      <div
        className="flex flex-col gap-3 overflow-y-auto snap-y snap-mandatory overscroll-contain max-h-[calc(100dvh-14rem)] pr-1"
        role="group"
        aria-label="Quads — scroll vertically for more pads"
      >
        {groups.length === 0 && (
          <p className="text-[11px] font-mono text-content-tertiary text-center py-6">
            No pads yet — tap + Add on the pad grid to create one.
          </p>
        )}
        {groups.map((group, groupIdx) => (
          <div
            key={groupIdx}
            className="grid grid-cols-2 gap-3 snap-start shrink-0"
          >
            {group.map(pad => (
              <div
                key={pad.key}
                className="rounded-lg bg-surface-raised/40 border border-brd-subtle p-2"
              >
                <div
                  className={`text-[10px] font-mono uppercase tracking-wider mb-1.5 text-center truncate ${
                    pad.accent === 'second'
                      ? 'text-second'
                      : 'text-content-tertiary'
                  }`}
                >
                  {pad.label}
                </div>
                <div className="grid grid-cols-4 gap-1.5">
                  {Array.from({ length: 16 }).map((_, stepIdx) => {
                    if (stepIdx >= visibleSteps) {
                      const next = nextStepCountFor(stepIdx)
                      return (
                        <button
                          key={stepIdx}
                          type="button"
                          onClick={() => setStepCount(next)}
                          aria-label={`Extend to ${next} steps`}
                          title={`Extend to ${next} steps`}
                          className="aspect-square rounded-md border border-dashed border-brd-subtle/60 bg-surface-raised/20 opacity-50 hover:opacity-100 hover:border-[color:var(--accent-halo)] hover:bg-surface-raised/40 hover:text-accent transition-colors text-content-tertiary font-mono text-[9px] flex items-center justify-center"
                        >
                          {stepIdx + 1}
                        </button>
                      )
                    }
                    return (
                      <StepCellButton
                        key={stepIdx}
                        cell={pad.row[stepIdx]}
                        stepIndex={stepIdx}
                        isDownbeat={stepIdx % 4 === 0}
                        isPlayhead={isPlaying && currentStep === stepIdx}
                        accent={pad.accent}
                        trackerRow={pad.trackerRow}
                        onToggle={() => pad.onToggle(stepIdx)}
                      />
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </StepGridShell>
  )
}

// ── Shared cell button ──────────────────────────────────────

interface StepCellButtonProps {
  cell: StepCell | undefined
  stepIndex: number
  isDownbeat: boolean
  isPlayhead: boolean
  accent?: 'accent' | 'second'
  /**
   * Sizing mode.
   *   - 'square' (default) — `aspect-square`, size driven by column
   *     width. Used by 8×8 and 4×4 where the grid is explicitly sized
   *     so square cells fit inside their parent.
   *   - 'fill' — `w-full h-full`, size driven by the parent grid's
   *     row/column template. Used by 2×8 (Tracks mode) where the
   *     track row has a fixed height and columns are wider than the
   *     per-cell budget, so forcing `aspect-square` makes cells spill
   *     vertically and collide with adjacent tracks.
   */
  size?: 'square' | 'fill'
  /**
   * Pad this cell belongs to. When supplied, long-press (≥320ms) and
   * right-click / ctrl+click open the per-step inspector by writing
   * `{ row, stepIndex }` to `trackerFocus`. Omit for read-only or
   * placeholder uses. Every layout (2×8, 8×8, 4×4, tracker) passes
   * this so the inspector works uniformly — matches Polyend/Renoise
   * "cell has more than on/off" conventions.
   */
  trackerRow?: TrackerRow
  onToggle: () => void
}

const StepCellButton: React.FC<StepCellButtonProps> = ({
  cell,
  stepIndex,
  isDownbeat,
  isPlayhead,
  accent = 'accent',
  size = 'square',
  trackerRow,
  onToggle,
}) => {
  const setTrackerFocus = useSequencerStore(s => s.setTrackerFocus)
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const longPressFired = useRef(false)

  const on = cell?.on ?? false
  const rich = isRichStep(cell)
  const onClasses =
    accent === 'second'
      ? 'bg-second border-second text-[color:var(--second-ink,white)] font-bold'
      : 'bg-accent border-accent text-[color:var(--accent-ink)] font-bold shadow-[0_0_10px_var(--accent-halo)]'

  const openInspector = () => {
    if (!trackerRow) return
    setTrackerFocus({ row: trackerRow, stepIndex })
  }

  const handlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    longPressFired.current = false
    if (!trackerRow) return
    // Desktop shortcut: right-click or ctrl/cmd-click opens inspector
    // immediately without waiting for the hold timer.
    if (e.button === 2 || e.ctrlKey || e.metaKey) {
      e.preventDefault()
      longPressFired.current = true
      openInspector()
      return
    }
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true
      openInspector()
    }, LONG_PRESS_MS)
  }

  const cancelLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }

  const handleClick = () => {
    // Long-press already dispatched openInspector; swallow the tap so
    // we don't toggle on/off in the same gesture.
    if (longPressFired.current) {
      longPressFired.current = false
      return
    }
    onToggle()
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      onPointerDown={handlePointerDown}
      onPointerUp={cancelLongPress}
      onPointerLeave={cancelLongPress}
      onPointerCancel={cancelLongPress}
      onContextMenu={e => {
        if (!trackerRow) return
        e.preventDefault()
        longPressFired.current = true
        openInspector()
      }}
      aria-pressed={on}
      aria-label={`Step ${stepIndex + 1}${on ? ' (active)' : ''}`}
      className={[
        size === 'fill' ? 'w-full h-full min-w-0 min-h-0' : 'aspect-square',
        'rounded-md border transition-colors flex items-center justify-center font-mono text-[10px] relative',
        on
          ? onClasses
          : isDownbeat
            ? 'bg-surface-raised border-brd-emphasis text-content-tertiary hover:border-[color:var(--accent-halo)]'
            : 'bg-surface-raised/40 border-brd-subtle text-content-tertiary/60 hover:border-[color:var(--accent-halo)]',
        isPlayhead ? 'outline outline-2 outline-second outline-offset-1' : '',
      ].join(' ')}
    >
      {stepIndex + 1}
      {rich && (
        <span
          aria-hidden
          className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-second"
          title="Per-step data — long-press or right-click to edit"
        />
      )}
      {/* Phase 3a — off-grid timing tick. Positioned at microOffset *
          100% of the cell width; the zero case (integer grid) is
          filtered by the condition so aligned notes don't carry the
          extra visual weight. */}
      {on &&
        cell?.microOffset !== undefined &&
        cell.microOffset > 0 &&
        cell.microOffset < 1 && (
          <span
            aria-hidden
            className="absolute bottom-0 w-0.5 h-1.5 bg-second"
            style={{ left: `${cell.microOffset * 100}%` }}
            title={`Off-grid: ${Math.round(cell.microOffset * 100)}% into the step`}
          />
        )}
    </button>
  )
}

// ── Shell ───────────────────────────────────────────────────

/**
 * Frame for every non-tracker step layout. Header carries the
 * layout title on the left and — when the layout supports it — a
 * step-count segment control on the right. The segment control
 * used to live in the Transport bar but it's really a property of
 * the pattern being edited, so it belongs next to the pattern's
 * name.
 */
const StepGridShell: React.FC<{
  title: string
  note?: string
  children: React.ReactNode
}> = ({ title, note, children }) => {
  const project = useSequencerStore(selectCurrentProject)
  const setStepCount = useSequencerStore(s => s.setStepCount)
  const extendByDuplication = useSequencerStore(
    s => s.extendCurrentPatternByDuplication,
  )
  const stepCount = project.stepCount
  const canDuplicate = stepCount < STEP_COUNTS[STEP_COUNTS.length - 1]
  const nextCount = STEP_COUNTS.find(c => c > stepCount) ?? stepCount
  return (
    <div className="rounded-xl bg-surface-sunken border border-brd-subtle px-4 py-4">
      <div className="flex items-center justify-between mb-3 gap-2">
        <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary truncate">
          {title}
        </span>
        <div className="flex items-center gap-2 flex-shrink-0">
          {note && (
            <span className="text-[10px] font-mono text-content-tertiary">
              {note}
            </span>
          )}
          <button
            type="button"
            onClick={extendByDuplication}
            disabled={!canDuplicate}
            title={
              canDuplicate
                ? `Extend to ${nextCount} steps by duplicating the current pattern (so the new half plays the same loop instead of being empty)`
                : 'Already at max length (128 steps)'
            }
            className="px-1.5 py-0.5 rounded text-[10px] font-mono border border-brd-subtle text-content-tertiary hover:border-[color:var(--accent-halo)] hover:text-accent disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            ×2
          </button>
          <div
            role="radiogroup"
            aria-label="Pattern length"
            className="inline-flex items-center gap-0.5 rounded-md border border-brd-subtle bg-surface-base p-0.5"
            title="Pattern length (steps). Clicking a disabled step in the grid also extends to cover it."
          >
            {STEP_COUNTS.map(count => {
              const active = stepCount === count
              return (
                <button
                  key={count}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setStepCount(count)}
                  className={`px-1.5 py-0.5 rounded text-[10px] font-mono transition-colors ${
                    active
                      ? 'bg-accent text-[color:var(--accent-ink)] font-semibold'
                      : 'text-content-secondary hover:text-content-primary'
                  }`}
                >
                  {count}
                </button>
              )
            })}
          </div>
        </div>
      </div>
      {children}
    </div>
  )
}

/** Smallest supported step count that covers the clicked index. */
function nextStepCountFor(stepIdx: number): StepCount {
  return (
    (STEP_COUNTS.find(c => c > stepIdx) as StepCount | undefined) ??
    (STEP_COUNTS[STEP_COUNTS.length - 1] as StepCount)
  )
}

// Re-export layout mode type so parents can forward it if needed.
export type { LayoutMode }
